import { useEffect, useState, useCallback } from 'react';
import {
  type ConsentCategories,
  type ConsentRecord,
  readStoredConsent,
  writeStoredConsent,
  hasGlobalPrivacyControl,
  ALLOW_ALL,
  DENY_ALL,
} from './consent';

// Custom events for cross-component coordination without a heavy store:
//   - CONSENT_EVENT fires when consent is updated (banner -> analytics loader)
//   - OPEN_SETTINGS_EVENT fires when "Nastavenia cookies" is clicked (footer
//     anywhere -> banner re-display)
const CONSENT_EVENT = 'dr:consent-updated';
const OPEN_SETTINGS_EVENT = 'dr:open-cookie-settings';

// Footer link can call this without hooking into useConsent in a parent
// scope. The banner instance hears the event and re-renders.
export function openConsentSettings(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
}

type UseConsentResult = {
  // null until we've read storage on mount; either a record or a "no decision
  // yet" state.
  consent: ConsentRecord | null;
  // true while we haven't read storage on first render (avoids banner flash).
  isHydrated: boolean;
  // true when banner should be visible (no stored decision and GPC isn't set).
  shouldShowBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  setCategories: (categories: ConsentCategories) => void;
  openSettings: () => void; // forces banner re-display (footer link)
};

export function useConsent(): UseConsentResult {
  const [consent, setConsent] = useState<ConsentRecord | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [forceBanner, setForceBanner] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setConsent(stored);
    } else if (hasGlobalPrivacyControl()) {
      // GPC signal: persist a DENY_ALL record so subsequent visits skip the
      // banner. Treats GPC as the legally valid expression of refusal.
      const record = writeStoredConsent(DENY_ALL);
      setConsent(record);
    }
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    function onUpdate(e: Event) {
      const detail = (e as CustomEvent<ConsentRecord | null>).detail;
      setConsent(detail);
      if (detail) setForceBanner(false);
    }
    function onOpenSettings() {
      setForceBanner(true);
    }
    window.addEventListener(CONSENT_EVENT, onUpdate);
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onUpdate);
      window.removeEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
    };
  }, []);

  const dispatch = useCallback((record: ConsentRecord) => {
    window.dispatchEvent(
      new CustomEvent<ConsentRecord>(CONSENT_EVENT, { detail: record }),
    );
  }, []);

  const acceptAll = useCallback(() => {
    const record = writeStoredConsent(ALLOW_ALL);
    dispatch(record);
  }, [dispatch]);

  const rejectAll = useCallback(() => {
    const record = writeStoredConsent(DENY_ALL);
    dispatch(record);
  }, [dispatch]);

  const setCategories = useCallback(
    (categories: ConsentCategories) => {
      const record = writeStoredConsent(categories);
      dispatch(record);
    },
    [dispatch],
  );

  const openSettings = useCallback(() => {
    setForceBanner(true);
  }, []);

  const shouldShowBanner = isHydrated && (forceBanner || consent === null);

  return { consent, isHydrated, shouldShowBanner, acceptAll, rejectAll, setCategories, openSettings };
}
